/**
 * @module nhcmap/src/overlays/PopupOverlay
 */
/**
 * @dependencies jquery, jquery-ui and pagination.js
 * The CSS for jquery-ui and pagination.js is imported by PopupOverlay.css
 */
import './PopupOverlay.css';
import Overlay from 'ol/Overlay.js';

/**
 * @classdesc
 * PopupOverlay is a class that creates a popup overlay for the map.
 */

export class PopupOverlay extends Overlay {
  /**
   * Create a popup overlay.
   * @param {Object} [opt_options] Control options.
   */
  constructor(opt_options) {
    const options = opt_options || {};

    // The element that will contain the control needs to be created here
    // and passed to the parent constructor.
    options.element = document.createElement('div');
    super(options);

    this.options = options;

    /*
    Structure of the popup container:
    <div id="popup" class="nhc-popup">
      <div>
        <button class="nhc-popup-closer">...</button>
      </div>
      <div id="popup-content" class="nhc-popup-content">
        <div id="popup-data"></div>
        <hr>
        <div id="popup-pagination" class="nhc-pagination"></div>
      </div>
    </div>
    */

    // Create the inner-most divs (including the hr div)
    this.popupData = document.createElement('div');
    this.popupData.id = 'popup-data';
    this.hr = document.createElement('hr');
    this.popupPagination = document.createElement('div');
    this.popupPagination.id = 'popup-pagination';
    this.popupPagination.className = 'nhc-pagination';

    // Create the content div and append its child elements
    this.popupContent = document.createElement('div');
    this.popupContent.id = 'popup-content';
    this.popupContent.appendChild(this.popupData);
    if (this.options.pagination) {
      //this.popupContent.appendChild(this.hr);
      this.popupContent.appendChild(this.popupPagination);
    }

    // Create the anchor element
    this.popupCloser = document.createElement('div');
    this.popupCloser.innerHTML = `
      <button id="close-button" class="usa-button usa-button--unstyled nhc-popup-closer">
        <span>Close popup</span>
        <svg class="usa-icon" aria-hidden="true" focusable="false" role="img">
          <use xlink:href="/assets/img/sprite.svg#close"></use>
        </svg>
      </button>
    `;

    // Create the main div and append its child elements
    // this.element = document.createElement('div');
    // Note: this.element is created when super(options) is called above
    this.element.id = this.options.elementId;
    this.element.className = 'nhc-popup';
    this.element.appendChild(this.popupCloser);
    this.element.appendChild(this.popupContent);

    // Store popup configurations as an array of objects
    this.popups = [];
    this.templates = {};

  }

  /**
   * Overriding the setMap method to add the event listeners. This ensures
   * that the event listeners are added after the control is added to the map.
   * @param {object} map 
   */
  setMap(map) {
    // Call the parent method.
    super.setMap(map);
  
    // Register the event handler if the map is not null.
    if (map) {
      // Display the popup when clicking on a feature
      map.on('singleclick', (event) => {
        this.displayFeatureInfo(map, event);
      });
      // Close the popup when the close button is clicked
      this.popupCloser.addEventListener('click', (event) => {
        this.setPosition(undefined);
        this.popupCloser.blur();
        event.stopPropagation();
        event.preventDefault();
        return false;
      });
    }
  }

  // Part of the goal of this method is to pull in some layer info dynamically
  // so we don't have to have redundant information in the config.
  async addPopups(productConfig) {
    if (!this.nunjucks) {
      // Dynamically import nunjucks and cache it on the instance
      const nunjucksModule = await import('nunjucks');
      this.nunjucks = nunjucksModule.default || nunjucksModule;
    }
    for (const popupConfig of productConfig.popups) {
      const layerLabels = productConfig.layers.reduce((acc, layer) => {
        acc[layer.id] = layer.labelShort;
        return acc;
      }, {});
      popupConfig.layerLabels = layerLabels;
      this.popups.push(popupConfig);
      this.templates[popupConfig.id] = this.nunjucks.compile(popupConfig.template);
    };
  }

  removePopups(productConfig) {
    const popupConfig = productConfig.popups;
    this.popups = this.popups.filter(popup => popup.id !== popupConfig.id);
    delete this.templates[popupConfig.id];
  }

  /**
   * Display the feature info in the popup
   * @param {object} map The map object from the main map module
   * @param {object} event The event object from the map module
   * @param {object} checks The checks object from the main map module
   */
  displayFeatureInfo(map, event) {
    // Close any existing overlay. If click is outside the region
    // this is faster than waiting to find out if there is a feature
    this.setPosition(undefined);

    const pixel = map.getEventPixel(event.originalEvent);
    const features = [];
    const sources = [];
    map.forEachFeatureAtPixel(pixel, (feature, layer) => {
      const popup = this.popups.find(popup => {
        return popup.layers.includes(layer.get('name'));
      });
      if (!popup) return;
      sources.push(popup.layerLabels[layer.get('name')]);
      features.push({ feature, popup });
    }, {
      layerFilter: function (layer) {
        return (typeof layer.getSource().url_ !== "undefined") ? true : false;
      },
    });

    if (features.length > 0) {
      const info = this.gatherInfo(features, sources);
      if (info.length > 0) {
        // Reset data and pagination controls
        $("#popup-data").html('');
        $("#popup-pagination").html('');

        // Update content in your overlay
        if (this.options.pagination) {
          $("#popup-pagination").pagination({
            dataSource: Object.values(info),
            pageSize: 1,
            showPageNumbers: false,
            showNavigator: true,
            callback: (data, pagination) => {
              if (pagination.totalNumber === 1) {
                $("#popup-pagination").hide();
              }
              else {
                $("#popup-pagination").show();
            }
            $("#popup-data").html(data);
            }
          });
        }
        else {
          $("#popup-data").html(info[0]);
        }
        this.setPosition(event.coordinate);
      }
    }
  };

  /**
   * Gather the info from the features and sources. Different data sources
   * store data in different ways, so this function is designed to handle
   * different data sources.
   * @param {object} features 
   * @param {array} sources 
   * @returns 
   */
  gatherInfo(features, sources) {
    const info = [];
    let i, ii;
    
    for (i = 0, ii = features.length; i < ii; ++i) {
      const { feature, popup } = features[i];
      // The breakpoints data, for example, is stored in an HTML table in the
      // description field of the feature. The keys are defined in the dataKeys
      // option. The dataKeys option is an array of strings that correspond to
      // the keys in the table.
      if (popup.dataSource === "description") {
        const description = feature.get('description');
        const parsed = this.parseDescription(description, popup.dataKeys);
        parsed['Source'] = sources[i];
        // Creating an object that we can loop through in the template
        parsed['items'] = {};
        for (let key in parsed) {
          if (key === "items") continue;
          parsed["items"][key] = parsed[key];
        }
        info.push(this.templates[popup.id].render(parsed));
      }
      // The Rip Currents data, for example, is stored in the properties field
      // of the feature. The keys we want to use are defined in the dataKeys
      // option.
      else if (popup.dataSource === "properties") {
        const parsed = feature.getProperties();
        // reduce parsed to the keys in dataKeys
        const keys = popup.dataKeys;
        const reduced = {};
        for (let key of keys) {
          reduced[key] = parsed[key];
        }
        reduced['Source'] = sources[i];
        // Creating an object that we can loop through in the template
        reduced['items'] = {};
        for (let key in reduced) {
          if (key === "items") continue;
          reduced["items"][key] = reduced[key];
        }
        info.push(this.templates[popup.id].render(reduced));
      }
      
    }
    
    return info;
  }

  /**
   * Parse the description from the feature. This is similar to how nhcWeb 
   * parses text products. It assumes that the description is in the form of
   * a table with the keys in the first column and the values in the second
   * column. The keys are defined in the dataKeys option.
   * @param {string} description
   * @param {array} keys - The keys to search for in the description
   * @returns {object} The parsed object
   */
  parseDescription(description, keys) {
    if (!description) return {};
    const parsed = {};
    for (let key of keys) {
      parsed[key] = "";
    }

    // start by splitting the text into lines & eliminating blank lines
    const lines = description.split(/\r?\n/).filter(item => 
      item.trim().length > 0
    );

    for (let i = 0; i < lines.length; i++) {
      // check if this line contains a key. If it does, check the next line and
      // search for the value in a regular expression of /<td>(.*)</td>/g
      for (let j = 0; j < keys.length; j++) {
        const regex = new RegExp(`<td>${keys[j]}<\\/td>`, 'i');
        const found = lines[i].match(regex);
        if (found) {
          let value = lines[i + 1].match(/<td>(.*)<\/td>/g);
          if (value) {
            parsed[keys[j]] = value[0].replace(/<td>/g, "").replace(/<\/td>/g, "");
          }
        }
      }
    }
    return parsed;
  }

}
